/**
 * Who the player thinks is sitting across from them.
 *
 * ============================ WHY THIS FILE EXISTS ==============================
 * The seat used to be labelled with its ARCHETYPE. A player sat down and read
 * "Calling Station" on seat four, and the single most valuable thing this product
 * teaches — noticing what kind of player someone is from how they act — was already
 * done for them, for free, before the first hand.
 *
 * `engine/bot/profile.ts` says it in its own header: "you have to learn to beat a
 * calling station and a maniac differently." You cannot learn to SPOT one that is
 * wearing a name badge. The label was the answer key printed on the cover.
 *
 * So the table shows a person, and what that person is playing stays on our side of
 * the wire until the session ends and the reveal earns it.
 *
 * ## The assignment is deliberately uncorrelated
 *
 * The tempting version pairs a name to a temperament — the jovial drunk is always the
 * whale, the undertaker is always the rock. It reads beautifully and it rebuilds the
 * exact leak it was meant to close, one session later, once the player notices that
 * Preacher Job has never once bluffed. A name that predicts a style is a label with
 * extra steps.
 *
 * The mapping is therefore drawn from the session seed and is uncorrelated with the
 * archetype by construction. `tests/cast.test.ts` measures that it stays that way.
 *
 * ## Names
 *
 * Invented. No real person, living or dead, and nothing lifted from a film or a novel
 * — these appear beside a product people pay for, and a borrowed name is someone
 * else's to license. Period-plausible frontier naming: plain Anglo and Scots-Irish
 * surnames, Old Testament given names, and the occasional earned nickname, which is
 * where a table gets its texture.
 * ================================================================================
 */

import { CryptoRng, type CryptoSeed } from "./rng";
import { seedBytes, shuffleInPlace } from "./deck";

export interface CastMember {
  id: string;
  /** Full name, shown where there is room for one. */
  name: string;
  /**
   * The seat box on a nine-handed table at 390px, and nothing wider.
   *
   * The archetype names failed exactly here: "Calling Station" wrapped to three lines
   * and overlapped the seats beside it. One word, and a short one.
   */
  short: string;
  /**
   * Id into the curated portrait set, or null for the placeholder silhouette.
   *
   * Null for every member today: the portraits are briefed and not yet produced, and
   * a seat drawn with a blank frame is honest where a stock photograph would not be.
   */
  avatarId: string | null;
}

const m = (id: string, name: string, short: string): CastMember =>
  ({ id, name, short, avatarId: null });

/**
 * The roster.
 *
 * Comfortably more than the nine a full table needs, so that two consecutive sessions
 * rarely share a face and the cast does not start to feel like a repertory company.
 * Short names are unique across the whole roster — a table with two "Amos" in it is a
 * bug the player experiences as the game losing track of who is who.
 */
export const CAST: readonly CastMember[] = [
  m("creed",     "Amos Creed",        "Amos"),
  m("halloran",  "Della Halloran",    "Della"),
  m("mercer",    "Josiah Mercer",     "Josiah"),
  m("vance",     "Ruth Vance",        "Ruth"),
  m("tolliver",  "Ezra Tolliver",     "Ezra"),
  m("shaw",      "Minnie Shaw",       "Minnie"),
  m("bridger",   "Silas Bridger",     "Silas"),
  m("quinn",     "Orla Quinn",        "Orla"),
  m("deadeye",   "Deadeye Hollis",    "Deadeye"),
  m("calloway",  "Nate Calloway",     "Nate"),
  m("pike",      "Hattie Pike",       "Hattie"),
  m("stroud",    "Abel Stroud",       "Abel"),
  m("preacher",  "Preacher Job",      "Preacher"),
  m("lamont",    "Cora Lamont",       "Cora"),
  m("finch",     "Wilbur Finch",      "Wilbur"),
  m("ashby",     "Nell Ashby",        "Nell"),
  m("doc",       "Doc Rennick",       "Doc"),
  m("bell",      "Gideon Bell",       "Gideon"),
  m("marsh",     "Etta Marsh",        "Etta"),
  m("crow",      "Jonah Crow",        "Jonah"),
  m("teague",    "Verna Teague",      "Verna"),
  m("holt",      "Rufus Holt",        "Rufus"),
  m("kestrel",   "Sallie Kestrel",    "Sallie"),
  m("dunmore",   "Cyrus Dunmore",     "Cyrus"),
  m("whitlock",  "Pearl Whitlock",    "Pearl"),
  m("agnew",     "Thaddeus Agnew",    "Thad"),
  m("river",     "Lucinda River",     "Lucinda"),
  m("bosley",    "Otis Bosley",       "Otis"),
];

export const CAST_BY_ID: ReadonlyMap<string, CastMember> =
  new Map(CAST.map((c) => [c.id, c]));

export function castById(id: string): CastMember {
  const c = CAST_BY_ID.get(id);
  if (!c) throw new Error(`unknown cast member: ${id}`);
  return c;
}

/* ------------------------------------------------------------- assignment --- */

/**
 * Deal the cast for a session.
 *
 * Deterministic from the session seed, like everything else the table does: the same
 * seed reproduces the same table, which is what lets a hand be replayed rather than
 * merely described, and what the fairness page depends on.
 *
 * The domain is `"cast"`, a different label from the deck's, so the identities are
 * drawn from an independent stream. Sharing a stream would make the cast a function
 * of the cards — and, worse, make the cards recoverable from the cast, which is
 * visible to anyone at the table.
 *
 * Reuses `shuffleInPlace` and `CryptoRng.below` rather than picking indices with a
 * modulo. Same reasoning as the deck's, and it is already tested to be unbiased over
 * every permutation at small sizes; a second, hand-rolled picker here would be an
 * untested reimplementation of a thing we got right once.
 */
export function castFor(count: number, seed: CryptoSeed): CastMember[] {
  if (!Number.isInteger(count) || count < 0) {
    throw new Error(`a table cannot seat ${count}`);
  }
  if (count > CAST.length) {
    // Rather than repeating a face. Two seats with one name is a bug the player
    // experiences as the game losing track of who is who.
    throw new Error(`the cast has ${CAST.length} members; ${count} were asked for`);
  }
  const pool = shuffleInPlace([...CAST], new CryptoRng(seedBytes(seed), "cast"));
  return pool.slice(0, count);
}
